import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Tab,
  Tabs,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';
import { useAuth } from '../state/AuthContext';
import { useToast } from '../state/ToastContext';
import { useTranslation } from 'react-i18next';
import { parseErrorMessage } from '../utils/errorUtils';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CustomButton from '../components/CustomButton';

import { Device, DeviceStats } from '../types';

export default function RegisteredDevicesPage() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const { t } = useTranslation();
  const [devices, setDevices] = useState<Device[]>([]);
  const [stats, setStats] = useState<DeviceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [deviceRevocationDuration, setDeviceRevocationDuration] = useState<number | null>(1440);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const [tab, setTab] = useState(0);

  const isMasterUser = user?.role === 'master_full';

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const includeRevoked = tab === 1;
      const res = await api.listDevices(null, includeRevoked, { includeDetails: false });

      if (tab === 1) {
        setDevices((res.data || []).filter((d) => !!d.revoked_at));
      } else {
        setDevices(res.data || []);
      }

      setStats(res.stats);
    } catch (error) {
      showError(parseErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, 60000); // Poll every 60s
    return () => clearInterval(interval);
  }, [tab]);

  const handleRevoke = async () => {
    if (!revokeId) return;
    try {
      await api.revokeDevice(null, revokeId, {
        durationMinutes: deviceRevocationDuration,
      });
      showSuccess(t('registeredDevices.toast.revoked'));
      fetchDevices();
    } catch (error) {
      showError(parseErrorMessage(error));
    } finally {
      setRevokeId(null);
      setDeviceRevocationDuration(1440);
    }
  };

  const handleUnrevoke = async (id: string) => {
    try {
      await api.unrevokeDevice(null, id);
      showSuccess(t('registeredDevices.toast.restored'));
      fetchDevices();
    } catch (error) {
      showError(parseErrorMessage(error));
    }
  };

  const handleStartEdit = (device: Device) => {
    setEditingId(device.id);
    setEditName(device.device_name || device.platform || '');
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    try {
      await api.updateDeviceName(null, editingId, editName.trim());
      showSuccess(t('registeredDevices.toast.renamed'));
      setEditingId(null);
      setEditName('');
      fetchDevices();
    } catch (error) {
      showError(parseErrorMessage(error));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const getRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return t('registeredDevices.timeAgo.seconds', { count: diff });
    if (diff < 3600) return t('registeredDevices.timeAgo.minutes', { count: Math.floor(diff / 60) });
    if (diff < 86400) return t('registeredDevices.timeAgo.hours', { count: Math.floor(diff / 3600) });
    return t('registeredDevices.timeAgo.days', { count: Math.floor(diff / 86400) });
  };

  const getPlatformColor = (platform: string | null) => {
    if (!platform) return 'default';
    const p = platform.toLowerCase();
    if (p.includes('windows')) return 'info';
    if (p.includes('linux')) return 'warning';
    if (p.includes('mac') || p.includes('darwin')) return 'secondary';
    if (p.includes('android')) return 'success';
    if (p.includes('ios')) return 'primary';
    return 'default';
  };

  const activeDevices = devices.filter((d) => !d.revoked_at);
  const revokedDevices = devices.filter((d) => !!d.revoked_at);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          {stats && (
            <Typography variant="body2" color="text.secondary">
              {stats.active} {stats.active === 1 ? t('registeredDevices.stats.activeSingular') : t('registeredDevices.stats.activePlural')}
              {stats.limit && t('registeredDevices.stats.of') + stats.limit + t('registeredDevices.stats.allowed')}
              {stats.available !== null && stats.available > 0 && (
                <Chip
                  label={`${stats.available} ${stats.available === 1 ? t('registeredDevices.stats.availableSingular') : t('registeredDevices.stats.availablePlural')}`}
                  size="small"
                  color="success"
                  sx={{ ml: 1 }}
                />
              )}
              {stats.available === 0 && (
                <Chip label={t('registeredDevices.limitReached')} size="small" color="error" sx={{ ml: 1 }} />
              )}
            </Typography>
          )}
        </Stack>
        <Tooltip title={t('common.refresh')}>
          <span>
            <CustomButton
              variant="icon"
              icon={<RefreshIcon />}
              iconPosition="only"
              customSize={{ width: 36, height: 36 }}
              onClick={fetchDevices}
              disabled={loading}
            />
          </span>
        </Tooltip>
      </Stack>

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
            },
          }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('registeredDevices.tabs.active')}
                <Chip
                  label={tab === 0 ? devices.length : activeDevices.length}
                  size="small"
                  color="success"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('registeredDevices.tabs.revoked')}
                <Chip
                  label={tab === 1 ? devices.length : revokedDevices.length}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              </Box>
            }
          />
        </Tabs>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('registeredDevices.table.name')}</TableCell>
                  {isMasterUser && <TableCell>{t('registeredDevices.table.tenant')}</TableCell>}
                  <TableCell>{t('registeredDevices.table.application')}</TableCell>
                  <TableCell>{t('registeredDevices.table.user')}</TableCell>
                  <TableCell>{t('registeredDevices.table.platform')}</TableCell>
                  <TableCell>{t('registeredDevices.table.lastAccess')}</TableCell>
                  <TableCell>{t('registeredDevices.table.registeredAt')}</TableCell>
                  <TableCell align="right">{t('registeredDevices.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={isMasterUser ? 8 : 7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {t('registeredDevices.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {devices.map((device) => (
                  <TableRow key={device.id} hover>
                    <TableCell>
                      {editingId === device.id ? (
                        <Box display="flex" gap={1} alignItems="center">
                          <TextField
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder={t('registeredDevices.inlineEdit.placeholder')}
                            autoFocus
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleSaveEdit();
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                          />
                          <CustomButton
                            onClick={handleSaveEdit}
                            variant="primary"
                            disabled={!editName.trim()}
                          >
                            {t('registeredDevices.inlineEdit.save')}
                          </CustomButton>
                          <CustomButton onClick={handleCancelEdit} variant="secondary">
                            {t('registeredDevices.inlineEdit.cancel')}
                          </CustomButton>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {device.device_name || device.platform || t('registeredDevices.unnamed')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {device.device_id.substring(0, 8)}...
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    {isMasterUser && (
                      <TableCell>
                        {device.tenant ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {device.tenant.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {device.tenant.code}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {t('registeredDevices.unknown')}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {device.appName ? (
                        <Typography variant="body2" fontWeight={600}>
                          {device.appName}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {device.user ? (
                        <Box>
                          <Typography variant="body2">{device.user.display_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {device.user.username}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t('registeredDevices.unknown')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {device.platform ? (
                        <Chip
                          label={device.platform}
                          size="small"
                          color={getPlatformColor(device.platform)}
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t('registeredDevices.unknownFeminine')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {getRelativeTime(device.last_seen_at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(device.last_seen_at)}
                      </Typography>
                      {device.ip_address && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          IP: {device.ip_address}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(device.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {tab === 0 ? (
                        <>
                          <Tooltip title={t('common.rename')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleStartEdit(device)}
                              disabled={editingId === device.id}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.revoke')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setRevokeId(device.id)}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title={t('common.restore')}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleUnrevoke(device.id)}
                          >
                            <RestoreFromTrashIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={!!revokeId}
        title={t('registeredDevices.dialog.revokeTitle')}
        content={t('registeredDevices.dialog.revokeContent')}
        confirmText={t('registeredDevices.dialog.revokeConfirm')}
        onConfirm={handleRevoke}
        onClose={() => {
          setRevokeId(null);
          setDeviceRevocationDuration(1440);
        }}
        severity="error"
        customContent={
          <TextField
            fullWidth
            type="number"
            label={t('registeredDevices.dialog.durationLabel')}
            value={deviceRevocationDuration ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              if (value === '') {
                setDeviceRevocationDuration(null);
                return;
              }

              const parsed = Number.parseInt(value, 10);
              if (Number.isNaN(parsed) || parsed <= 0) {
                setDeviceRevocationDuration(null);
                return;
              }

              setDeviceRevocationDuration(parsed);
            }}
            helperText={t('registeredDevices.dialog.durationHelper')}
            size="small"
          />
        }
      />
    </Box>
  );
}
